import React, { useContext, Fragment, useState, useEffect } from "react";

import AppName from "AppName";
import Panel from "Atoms/Panel";
import Svg from "Atoms/Svg";
import Typography from "Atoms/Typography";
import Modal from "Components/Modal";
import ProductCard from "Components/ProductCard";
import { NavigationContext } from "Contexts/Navigation/NavigationContext";
import PanelContext from "Contexts/PanelContext";
import { TrackingContext } from "Contexts/TrackingContext/TrackingContext";
import { UserContext } from "Contexts/UserContext/UserContext";
import { VehicleContext } from "Contexts/VehicleContext";
import useKeypress, { KEYCODES } from "Hooks/useKepress";
import useWindow from "Hooks/useWindow";
import { updateRecentSearches } from "Layout/Header/RecentSearches/RecentSearches";
import SearchBar from "Molecules/SearchBar";
import Event from "Utilities/Event";
import ClassNames from "classnames";
import { get } from "idb-keyval";

import useBloomreach from "../../../Hooks/useBloomreach";
import useFeatureFlag from "../../../Hooks/useFeatureFlag/useFeatureFlag";
import { RecentSearches } from "../RecentSearches/RecentSearches";
import styles from "./SaytDropdown.module.scss";
import SaytItems from "./SaytItems";
import { buildNewSearchUrl } from "./SaytUtilities/setNewSearchUrl";
import SuggestedSearches from "./SuggestedSearches";
import SaytInitialContent from "./SaytInitialContent/SaytInitialContent";

const recentSearchesKey = "RecentSearches";
const SaytDropdown = ({
  searchTerm,
  setSearchTerm,
  className,
  mobilePlaceholder,
  desktopPlaceholder,
  services,
  ...props
}) => {
  const { dispatch: navigationDispatch, state: navigationState } =
    useContext(NavigationContext);
  const { segmentReady } = useContext(TrackingContext);
  const { context: userState } = useContext(UserContext);
  const { discovery } = navigationState;
  const { isSaytModalOpen } = discovery;

  const { sendBloomreachEvent } = useBloomreach();

  const [saytResults, setSaytResults] = useState(false);
  const { context: vehicleState } = useContext(VehicleContext);
  // one off
  const fakePanelContext = {
    brand: undefined,
    intent: undefined,
    inverseTheme: true,
    layer: 0,
  };

  const [currentUrl, setCurrentUrl] = useState("");
  const [initSearchTerm, setInitSearchTerm] = useState("");
  useWindow(() => {
    setCurrentUrl(window.location.href.toLowerCase());
  });
  let retainRecentSearchEnabled = useFeatureFlag("Retain_Search_Value_Enabled");
  useEffect(() => {
    if (
      searchTerm ||
      !currentUrl ||
      !currentUrl.includes("/search") ||
      !retainRecentSearchEnabled
    ) {
      return;
    }
    async function getRecentSearches() {
      try {
        let recentSearchesFromIndexDB = await get(recentSearchesKey);
        if (!recentSearchesFromIndexDB) {
          return;
        }
        setInitSearchTerm(recentSearchesFromIndexDB[0]);
        setSearchTerm(recentSearchesFromIndexDB[0]);
      } catch (err) {
        console.error(err);
      }
    }
    getRecentSearches();
  }, [currentUrl, retainRecentSearchEnabled]);

  useEffect(() => {
    if (!searchTerm || searchTerm == initSearchTerm) {
      setSaytResults(false);
      return;
    }

    let cleanSearch = searchTerm.trim();
    if (cleanSearch !== "") {
      let selectedVehicle = vehicleState.selectedVehicle;
      getSaytResults(
        searchTerm,
        services?.discovery?.url,
        userState.userGuid,
        userState.userVin(),
        selectedVehicle?.yearId,
        selectedVehicle?.makeId,
        selectedVehicle?.modelId,
        selectedVehicle?.raceTypeId
      ).then((saytResults) => {
        if (saytResults) setSaytResults(saytResults);
      });
    }
  }, [searchTerm]); //eslint-disable-line react-hooks/exhaustive-deps

  useKeypress(KEYCODES.escape, () => {
    setSearchTerm(initSearchTerm);
    navigationDispatch({
      type: "setDiscoveryState",
      payload: { discoveryState: { isSaytModalOpen: false } },
    });
  });

  if (!isSaytModalOpen) {
    return false;
  }

  let vehicleTitle = getSelectedVehicleTitle(vehicleState);

  const setNewSearchUrl = (searchTerm, searchUrl) => {
    sendBloomreachEvent({
      ptype: "suggest",
      action: "submit",
      data: {
        q: searchTerm,
        ref: window.location.href
      },
    });
    if (segmentReady) {
      analytics.track("Products Searched", {
        user_vin: `${userState.userVin()}`,
        web_user_guid: `${userState.userGuid}`,
        query: searchTerm,
        page_type: window.location.href,
        element: 'search_bar_header'
      });
    }
    updateRecentSearches(searchTerm);
    const newUrl = buildNewSearchUrl(searchTerm, searchUrl);
    window.location = newUrl;
  };

  const requestSearchUrl = (event) => {
    event.preventDefault();
    if (!searchTerm.trim()) {
      CloseSaytModal();
      return;
    }
    setNewSearchUrl(searchTerm, vehicleState.selectedVehicle.searchUrl);
  };
  const clearSearchTerm = () => {
    setSearchTerm("");
  };
  const CloseSaytModal = () => {
    setSearchTerm(initSearchTerm);
    navigationDispatch({
      type: "setDiscoveryState",
      payload: {
        discoveryState: {
          isSaytModalOpen: false,
        },
      },
    });
  };

  var topOfModalContent = (
    <Fragment>
      <div className={styles.searchBarTitleWrapper} onClick={CloseSaytModal}>
        <PanelContext.Provider value={fakePanelContext}>
          <Typography
            className={styles.transparentHeader}
            size={1}
            font={"heading"}
          >
            Search Speedway Motors
          </Typography>
        </PanelContext.Provider>
        <Panel className={styles.modalClose}>
          <Svg size={0.75} icon={"close"} data-testid={"modalCloseButton"} />
        </Panel>
      </div>
      <SearchBar
        iconSettings={{ size: 1.25, position: "end", filled: false }}
        className={ClassNames(className, styles.saytSearch, styles.hideMobile)}
        onChange={(s) => setSearchTerm(s)}
        handleSearch={(e) => requestSearchUrl(e)}
        clearSearchTerm={() => clearSearchTerm()}
        autofocus={true}
        value={searchTerm}
        textAhead={saytResults?.typeAheadSuggestion ?? null}
        isSaytModalOpen={isSaytModalOpen}
        placeholder={desktopPlaceholder}
        ymmOrRaceTypeValue={vehicleTitle}
        dataTesting={"saytSearchDesktop"}
        dataTestingSearchSubmit={"saytSearchDesktopSubmit"}
        isMobile={false}
      />

      {/* This addition below is only included because placeholder ellipsis are not able to function properly. Need a shrunken text... */}
      <SearchBar
        iconSettings={{ size: 1.25, position: "end", filled: false }}
        className={ClassNames(className, styles.saytSearch, styles.hideDesktop)}
        onChange={(e) => {
          setSearchTerm(e);
        }}
        handleSearch={(e) => requestSearchUrl(e)}
        clearSearchTerm={() => clearSearchTerm()}
        isSaytModalOpen={isSaytModalOpen}
        autofocus={true}
        textAhead={saytResults?.typeAheadSuggestion}
        value={searchTerm}
        placeholder={mobilePlaceholder}
        dataTesting={mobilePlaceholder}
        dataTestingSearchSubmit={"saytSearchMobileSubmit"}
        ymmOrRaceTypeValue={vehicleTitle}
        isMobile={true}
      />
      {!saytResults ? <div>
        <SaytInitialContent/>
      </div> : false}
    </Fragment>
  );

  let saytThings = "";

  if (
    searchTerm &&
    saytResults &&
    (saytResults.autoSuggestProducts?.length > 0 ||
      saytResults.autoSuggestArticles?.length > 0 ||
      saytResults.suggestedQueries?.length > 0)
  ) {
    saytThings = (
      <Panel className={styles.saytWrapper}>
        <div className={styles.suggestedSearchWrapper}>
          <SuggestedSearches
            searchTerm={searchTerm}
            saytResults={saytResults}
            searchUrl={vehicleState.selectedVehicle.searchUrl}
          />
        </div>
        <div className={styles.saytSecondColumnWrapper}>
          <SaytItems
            saytItems={saytResults?.autoSuggestProducts}
            type={"PRODUCT"}
          />
          {saytResults?.autoSuggestArticles &&
          saytResults.autoSuggestArticles.length > 0 ? (
            <SaytItems
              saytItems={saytResults?.autoSuggestArticles}
              type={"ARTICLE"}
              maxItemsToDisplay={3}
            />
          ) : null}
        </div>
      </Panel>
    );
  }

  try {
    return (
      <div>
        <Modal
          toggleOpen={(isSaytModalOpen) => {
            setSearchTerm(initSearchTerm);
            navigationDispatch({
              type: "setDiscoveryState",
              payload: {
                discoveryState: {
                  isSaytModalOpen: isSaytModalOpen,
                },
              },
            });
          }}
          open={isSaytModalOpen}
          allowOutsideClickClose
          contentAboveModal={topOfModalContent}
          alignItems={"flex-start"}
          className={styles.saytContainer}
        >
          {saytThings}
        </Modal>
      </div>
    );
  } catch (e) {
    console.error(e);
    return false;
  }
};

function getSaytResults(
  searchTerm,
  discoveryUrl,
  webUserGuid,
  userVin,
  year,
  makeId,
  modelId,
  raceTypeId
) {
  const query = `query GetAutoSuggestPublicResults($autoSuggestRequest: AutoSuggestPublicRequestInput) {
        autoSuggestPublic(autoSuggestPublicRequest: $autoSuggestRequest) {
          autoSuggestProducts {
                  position
                  price
                  url
                  skuBaseNumber
                  title
                  thumbImageUrl
                  publicSkuBaseDetails {
                    availability
                    reviewCount
                    rating
                    segmentCategorization
                    superMarketAffinity
                    brand
                  }
          }
          autoSuggestArticles{
              position
              url
              title
              thumbImageUrl
          }
          categoryLinks{
            url
            name
          }
          typeAheadSuggestion
          productQuery
              suggestedQueries {
                ordinality
                query,
                queryWithMarkups
              }
        }
      }`;

  let variables = {
    autoSuggestRequest: {
      query: searchTerm,
      numberOfSuggestions: 12,
      currentUrl: window.location.href,
      userVin,
      webUserGuid,
      ymmBoost: {
        year: year,
        make: makeId,
        model: modelId,
        raceTypeId: raceTypeId,
      },
    },
  };

  return requestSaytData(query, variables, discoveryUrl).then((result) => {
    return result?.data?.autoSuggestPublic;
  });
}

// this needs to be in an outer scope so that it's shared across requests
let abortController = null;

async function requestSaytData(query, variables, discoveryUrl) {
  try {
    let signal = null;
    if (window && window.AbortController) {
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();
      signal = abortController.signal;
    }
    const response = await fetch(discoveryUrl, {
      signal: signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
        variables: variables,
      }),
      credentials: "include",
    });

    var data = await response.json();

    if (data.errors) {
      data.errors.forEach((x) => console.error(x));
    }

    return data;
  } catch (error) {
    if (error.name === "AbortError") {
      return [];
    }

    console.error("Failed Request", error);
    return [];
  }
}

function getSelectedVehicleTitle(vehicleState) {
  let vehicle = vehicleState.selectedVehicle;
  let raceDisplay = vehicle?.raceDisplay;

  if (vehicle.yearId && vehicle.modelDisplay) {
    return `${vehicle.yearId} ${vehicle.modelDisplay}`;
  } else if (raceDisplay) {
    return raceDisplay;
  } else return false;
}

export default SaytDropdown;
